import React, { useState, useEffect, useRef } from 'react';
import {
  Grid,
  TextField,
  Select,
  MenuItem,
  Button,
  Box,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useAccount } from '../../contexts/Account';
import { useHistory } from 'react-router-dom';
import LoadingLogoSkeleton from '../../components/SkeletonLoading/loadingLogoSkeleton';
import ACSnackbar from '../../components/ACSnackbar';
import CompanyLogoUploader from '../../components/Company/LogoFreemiumUploader';
import {
  aobfStep1,
  uploadCompanyLogoCall,
  updateCompanyOnboardingStatusCall,
} from '../../services/company_services';
import logo from '../../images/logo_with_bg.png';

const styles = () => ({
  label: {
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '-0.4300000071525574px',
    color: '#646D7A',
    marginBottom: '8px',
  },
  info: {
    fontStyle: 'italic',
    fontSize: '14px',
    fontWeight: 300,
    lineHeight: '24px',
    letterSpacing: '-0.4300000071525574px',
    color: '#646D7A',
    marginBottom: '8px',
  },
  textField: {
    marginBottom: '8px',
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      '& fieldset': {
        borderColor: '#C7D0DE',
      },
    },
  },
  gridItemProps: {
    item: true,
    xxs: 12,
    md: 8,
  },
});

function getCompanyNameValue(accountDetails, asLoggedUserDetails) {
  const user = accountDetails.contacts.find(
    (contact) => contact.id === asLoggedUserDetails.userId
  );

  if (
    user &&
    accountDetails.companyName !== `${user.givenName} ${user.surName}`
  ) {
    return accountDetails.companyName;
  }
  return '';
}

const CompanyOnboardingStep1 = () => {
  const { accountDetails, asLoggedUserDetails } = useAccount();

  const isSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const isXS = useMediaQuery((theme) => theme.breakpoints.down('xs'));

  const classes = styles();
  const [plannedUsage, setPlannedUsage] = useState(
    accountDetails.plannedUsage || ''
  );
  const [skill, setSkill] = useState(
    accountDetails.desiredSkillsTagIds?.[0] || ''
  );
  const [headline, setHeadline] = useState(accountDetails.headline || '');
  const [companyName, setCompanyName] = useState(
    getCompanyNameValue(accountDetails, asLoggedUserDetails)
  );
  const [uploaderOutput, setUploaderOutput] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [openQuestionSnackbar, setOpenQuestionSnackbar] = useState(false);
  const history = useHistory();
  const plannedUsageOptions = [
    {
      label: "For my business (I'm a CEO/Founder/Owner)",
      value: 'For my organization',
    },
    { label: 'For my team at my company', value: 'For my team' },
    { label: 'For a personal side-project or my career', value: 'For myself' },
  ];

  const skillOptions = [
    { label: 'Marketing', value: 'sk_1021' },
    { label: 'Sales', value: 'sk_1083' },
    { label: 'Operations', value: 'sk_1672' },
    ,
    { label: 'HR', value: 'sk_1322' },
    { label: 'Product', value: 'sk_1343' },
    { label: 'Finance', value: 'sk_1670' },
    { label: 'General Leadership', value: 'sk_1674' },
    { label: 'Entrepreneurship', value: 'sk_921' },
    { label: 'Career', value: 'sk_1553' },
    { label: 'Other', value: 'none' },
  ];

  async function sendFormData() {
    try {
      await aobfStep1({
        CONTACT_ID: asLoggedUserDetails.userId,
        COMPANY_NAME:
          companyName !== accountDetails.companyName
            ? companyName.replace(/advisory board/i, '').trim()
            : '',
        PLANNED_USAGE: plannedUsage,
        SKILL_TAGS_IDS: skill === 'none' ? [] : [skill],
        HEADLINE: headline,
      });

      return {
        companyName: companyName,
        displayName: companyName,
        plannedUsage: plannedUsage,
        headline: headline,
      };
    } catch (err) {
      console.log(err);
      setOpenSnackbar(true);
    }
  }

  async function sendOriginalLogo() {
    try {
      if (
        !uploaderOutput ||
        !uploaderOutput.image ||
        !uploaderOutput.originalImage
      ) {
        convertPublicImageToBase64(logo).then(async (base64) => {
          const response = await uploadCompanyLogoCall({
            CONTACT_ID: asLoggedUserDetails.userId,
            COMPANY_LOGO_EDITED: base64,
            COMPANY_LOGO_ORIGINAL: uploaderOutput.originalImage || base64,
          });
          return {
            image: {
              id: response.data.uploadCompanyLogo,
              location: '',
            },
          };
        });
      }
      const response = await uploadCompanyLogoCall({
        CONTACT_ID: asLoggedUserDetails.userId,
        COMPANY_LOGO_EDITED: uploaderOutput.image,
        COMPANY_LOGO_ORIGINAL: uploaderOutput.originalImage,
      });
      return {
        image: {
          id: response.data.uploadCompanyLogo.id,
          location: '',
        },
      };
    } catch (err) {
      console.log(err);
      setOpenSnackbar(true);
    }
  }

  const convertPublicImageToBase64 = async (relativePath) => {
    const response = await fetch(relativePath);
    const blob = await response.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const disableButton = () => {
    if (isLoading || !plannedUsage || !skill || !companyName || !headline) {
      return true;
    }
    return false;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setIsLoading(true);

      const result = await Promise.all([
        sendFormData(),
        sendOriginalLogo(),
        updateCompanyOnboardingStatusCall({
          COMPANY_ID: accountDetails.id,
          ONBOARDING_STATUS: 'inProcess',
          IS_AOBF: true,
        }),
      ]);
      history.push('/company/onboarding/step2');
    } catch (error) {
      setIsLoading(false);
      setOpenSnackbar(true);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 2 }}>
      <Typography
        sx={{
          fontSize: isXS ? '24px' : '28px',
          fontWeight: 700,
          letterSpacing: '-0.43px',
          textAlign: 'center',
          mb: '24px',
        }}
      >
        Let's Set Up Your Advisory Board
      </Typography>
      <Grid>
        <Grid {...classes.gridItemProps}>
          <Typography sx={classes.label}>
            How are you planning on using your advisory board?
          </Typography>
        </Grid>
        <Grid {...classes.gridItemProps} mb="8px">
          <Select
            fullWidth
            value={plannedUsage}
            onChange={(e) => setPlannedUsage(e.target.value)}
            displayEmpty
            sx={{
              marginBottom: '8px',
              borderRadius: '10px',
            }}
          >
            <MenuItem value="" disabled>
              Select one
            </MenuItem>
            {plannedUsageOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid {...classes.gridItemProps}>
          <Typography sx={classes.label}>
            What types of experience would be most helpful to have on your
            advisory board?
          </Typography>
        </Grid>
        <Grid {...classes.gridItemProps} mb="8px">
          <Select
            fullWidth
            value={skill}
            onChange={(e) => setSkill(e.target.value)}
            displayEmpty
            sx={{
              marginBottom: '8px',
              borderRadius: '10px',
            }}
          >
            <MenuItem value="" disabled>
              Select one
            </MenuItem>
            {skillOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid {...classes.gridItemProps}>
          <Typography sx={classes.label}>
            Make your advisory board sound exciting! Create a one-sentence
            description for others
          </Typography>
        </Grid>
        <Grid {...classes.gridItemProps} mb="8px">
          <TextField
            fullWidth
            placeholder="We recommend sharing one credibility point and one exciting aspiration such as:
Previous Google engineer launching a startup to help doctors diagnosis health issues sooner.
Top 5 rated bakery in Boston, our mission is to serve french-level baked goods to our wonderful community. 
With an MBA from Penn, I’m gathering ideas on how to start an app to for rescuing dogs. 
Marketing team at Fortune 500 company seeks outside perspectives on how to use AI to connect with customers."
            value={headline}
            onChange={(e) => setHeadline(e.target.value)}
            inputProps={{
              maxLength: 200,
            }}
            sx={classes.textField}
            multiline
            maxRows={5}
            minRows={5}
          />
        </Grid>

        <Grid {...classes.gridItemProps}>
          <Typography sx={classes.label}>
            Enter a name for your advisory board:
          </Typography>
          <Typography sx={classes.info}>
            i.e. Parentfile Advisory Board, Marketing Advisory Board at Vibe
            Bikes, Jon Frank's Advisory Board
          </Typography>
        </Grid>
        <Grid {...classes.gridItemProps} mb="8px">
          <TextField
            fullWidth
            placeholder="ex: Acme Inc. Advisory Board"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            sx={classes.textField}
          />
        </Grid>
        <Grid {...classes.gridItemProps} mb="8px">
          <CompanyLogoUploader
            resultFiles={setUploaderOutput}
            status={() => {}}
            emptyLogoImage
            imageOnly
          />
        </Grid>

        <Grid
          {...classes.gridItemProps}
          md={5}
          display="flex"
          justifyContent="center"
        >
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={disableButton()}
            sx={{
              borderRadius: '50px',
              backgroundColor: '#3171F6',
              '&:hover': {
                backgroundColor: '#3171F6',
              },
              height: '48px',
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: 700,
              maxWidth: '450px',
              mb: isSM ? '24px' : null,
              my: '20px',
            }}
          >
            {isLoading ? <LoadingLogoSkeleton /> : 'Next'}
          </Button>
        </Grid>
      </Grid>
      <ACSnackbar
        onClose={() => setOpenSnackbar(false)}
        open={openSnackbar}
        severity="error"
        text="Something went wrong"
      />
      <ACSnackbar
        onClose={() => setOpenQuestionSnackbar(false)}
        open={openQuestionSnackbar}
        severity="info"
        text="Your current challenge or goal must be at least 15 characters"
      />
    </Box>
  );
};

export default CompanyOnboardingStep1;
